import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styles from './UserDetail.module.css'; //의도된 것임!
import { useApi } from '../../../hooks/ApiProvider';
import Confirm from '../../Modals/Confirm';
import { EditIcon, RegenerateIcon, DeleteIcon, SaveIcon, CancelIcon } from '../../../assets/icons';

// 챕터 데이터 구조 인터페이스
interface ChapterSection {
  index: number;
  title: string;
  paragraphs: string[];
}

interface ChapterContent {
  title?: string;
  sections: ChapterSection[];
}

// 서버 데이터 구조에 맞는 인터페이스
interface AssociatedFile {
  path: string;
  name: string;
  size: number;
  type: string;
  chapter_content?: ChapterContent | string;
  chat_content?: string;
  file_created_at?: string;
  file_modified_at?: string;
}

// 채팅 이벤트 인터페이스
interface ChatEvent {
  event: string;
  data: any;
}

interface Interview {
  id: number;
  session_id: string;
  file_path: string;
  created_at: string;
  updated_at: string;
  file_exists: boolean;
  file_size: number;
  file_size_mb: number;
  associated_files: AssociatedFile[];
}

interface ProjectInfo {
  id: number;
  title: string;
  description: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

interface History {
  id: number;
  session_code: string;
  action: number;
  created_at: string;
}

interface Image {
  id: number;
  file_path: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface InterviewDetailData {
  project_info: ProjectInfo;
  episode_id: number;
  interviews: Interview[];
  interview_count: number;
  is_chapter_generated: boolean;
  histories: History[];
  history_count: number;
  images: Image[];
  image_count: number;
}

const InterviewDetail: React.FC = () => {
  const { userId, projectId, episodeId } = useParams<{
    userId: string;
    projectId: string;
    episodeId: string;
  }>();
  
  const navigate = useNavigate();
  const location = useLocation();
  const { getInterviewDetail, updateInterview, deleteInterview, regenerateInterview, deleteInterviewFile, saveInterviewFile, generateInterviewPDF } = useApi();
  
  const [interviewData, setInterviewData] = useState<InterviewDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<InterviewDetailData>>({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteFileConfirm, setShowDeleteFileConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string>('');
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [editingFile, setEditingFile] = useState<string>('');
  const [editingContent, setEditingContent] = useState<string>('');
  const [savingFile, setSavingFile] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // 챕터 재생성 중 상태
  // const [regeneratingChapters, setRegeneratingChapters] = useState<Set<string>>(new Set());

  const loadInterviewData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // API 호출 (실제 API가 구현되면 이 부분을 수정)
      const response = await getInterviewDetail?.(userId!, projectId!, episodeId!);
      
      if (response?.success) {
        setInterviewData(response.data);
        setEditForm(response.data);
      } else {
        setError('인터뷰 데이터를 불러올 수 없습니다.');
      }
    } catch (err) {
      setError('인터뷰 데이터 로딩 중 오류가 발생했습니다.');
      console.error('Interview loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, projectId, episodeId, getInterviewDetail]);

  useEffect(() => {
    if (userId && projectId && episodeId) {
      loadInterviewData();
    }
  }, [userId, projectId, episodeId, loadInterviewData]);

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm(interviewData || {});
  };

  const handleSave = async () => {
    if (!interviewData) return;

    try {
      setSaving(true);
      
      const response = await updateInterview?.(userId!, projectId!, episodeId!, editForm);
      
      if (response?.success) {
        setInterviewData({ ...interviewData, ...editForm });
        setIsEditing(false);
        setError(null);
      } else {
        setError('저장에 실패했습니다.');
      }
    } catch (err) {
      setError('저장 중 오류가 발생했습니다.');
      console.error('Interview update error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async (result: 'Yes' | 'No') => {
    setShowDeleteConfirm(false);
    
    if (result === 'No' || !interviewData) {
      return;
    }

    try {
      setSaving(true);
      
      const response = await deleteInterview?.(userId!, projectId!, episodeId!);
      
      if (response?.success) {
        navigate(-1); // 뒤로가기
      } else {
        setError('삭제에 실패했습니다.');
      }
    } catch (err) {
      setError('삭제 중 오류가 발생했습니다.');
      console.error('Interview delete error:', err);
    } finally {
      setSaving(false);
    }
  };

  // 이전 페이지 정보 가져오기
  const fromPage = location.state?.from;
  const fromPageName = location.state?.fromName;

  // 뒤로가기 처리
  const handleGoBack = () => {
    console.log('handleGoBack called');
    console.log('fromPage:', fromPage);
    console.log('userId:', userId);
    
    let targetPath: string;
    
    if (fromPage && fromPage !== `/admin/users/detail/${userId}`) {
      console.log('Navigating to fromPage:', fromPage);
      targetPath = fromPage;
    } else {
      console.log('Navigating to user detail:', `/admin/users/detail/${userId}`);
      targetPath = `/admin/users/detail/${userId}`;
    }
    
    try {
      navigate(targetPath);
      console.log('navigate function called successfully');
    } catch (error) {
      console.error('navigate function failed:', error);
      console.log('Falling back to window.location.href');
      window.location.href = targetPath;
    }
  };

  // 뒤로가기 버튼 텍스트 결정
  const getBackButtonText = () => {
    if (fromPage && fromPage !== `/admin/users/detail/${userId}`) {
      return `← ${fromPageName || '이전 페이지'}로 돌아가기`;
    }
    return '← 목록으로 돌아가기';
  };

  // 파일 크기 포맷팅
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 액션 타입에 따른 텍스트
  const getActionText = (action: number): string => {
    switch (action) {
      case 0: return '시작';
      case 1: return '진행중';
      case 2: return '완료';
      default: return '알 수 없음';
    }
  };

  // 채팅 내용 파싱 및 포맷팅
  const parseChatContent = (chatContent: string): ChatEvent[] => {
    try {
      return chatContent
        .trim()
        .split('\n')
        .filter(line => line.trim())
        .map(line => {
          try {
            return JSON.parse(line);
          } catch (e) {
            console.warn('Invalid JSON line:', line);
            return null;
          }
        })
        .filter(Boolean) as ChatEvent[];
    } catch (e) {
      console.error('Error parsing chat content:', e);
      return [];
    }
  };

  // 채팅 이벤트 타임스탬프 포맷팅
  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('ko-KR');
  };

  // 챕터 재생성 핸들러
  const handleRegenerateChapter = async () => {
    const response = await regenerateInterview?.(userId!, projectId!, episodeId!);
    if (response?.success) {
      alert('챕터가 성공적으로 재생성되었습니다!');
      await loadInterviewData();
    } else {
      alert('챕터 재생성 중 오류가 발생했습니다.');
    }
  };

  // PDF 생성 핸들러
  const handleGeneratePDF = async () => {
    if (!projectId || !episodeId) return;

    try {
      setGeneratingPDF(true);
      const response = await generateInterviewPDF?.(parseInt(projectId), parseInt(episodeId), false);
      
      if (response?.success) {
        alert('PDF가 성공적으로 생성되었습니다!');
      } else {
        alert('PDF 생성 중 오류가 발생했습니다.');
      }
    } catch (err) {
      alert('PDF 생성 중 오류가 발생했습니다.');
      console.error('PDF generation error:', err);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDeleteFile = async (filepath: string) => {
    setFileToDelete(filepath);
    setShowDeleteFileConfirm(true);
  };

  const handleDeleteFileConfirm = async (result: 'Yes' | 'No') => {
    setShowDeleteFileConfirm(false);
    
    if (result === 'No' || !fileToDelete) {
      return;
    }

    try {
      const file_name = fileToDelete.split('/').pop() || '';
      const response = await deleteInterviewFile?.(userId!, projectId!, episodeId!, file_name);
      
      if (response?.success) {
        await loadInterviewData(); // 데이터 새로고침
      } else {
        alert('파일 삭제에 실패했습니다.');
      }
    } catch (err) {
      alert('파일 삭제 중 오류가 발생했습니다.');
      console.error('File delete error:', err);
    }
  };

  // 챕터 내용을 구조화된 형태에서 텍스트로 변환
  const parseChapterToText = (chapterContent: ChapterContent | string): string => {
    try {
      console.log('parseChapterToText input:', chapterContent);
      
      let chapterData = chapterContent;
      
      // 문자열인 경우 JSON 파싱
      if (typeof chapterContent === 'string') {
        try {
          chapterData = JSON.parse(chapterContent);
        } catch (e) {
          // JSON 파싱 실패 시 원본 반환
          return chapterContent;
        }
      }
      
      if (!chapterData || typeof chapterData !== 'object') {
        return String(chapterContent);
      }
      
      console.log('Parsed chapter data:', chapterData);
      
      let text = '';
      
      // 제목이 있으면 추가
      if (chapterData.title) {
        text += `# ${chapterData.title}\n\n`;
        console.log('Added title:', chapterData.title);
      }
      
      // 섹션들이 있으면 처리
      if (chapterData.sections && Array.isArray(chapterData.sections)) {
        console.log('Processing sections:', chapterData.sections);
        
                 chapterData.sections.forEach((section: ChapterSection, index: number) => {
           console.log(`Processing section ${index}:`, section);
           
           // 섹션 제목이 있으면 추가 (편집자 메모는 특별 처리)
           if (section.title && section.title !== '편집자 메모') {
             text += `## ${section.title}\n\n`;
             console.log('Added section title:', section.title);
           } else if (section.title === '편집자 메모') {
             text += `## 📝 ${section.title}\n\n`;
             console.log('Added editor memo section:', section.title);
           }
                       // 제목이 없는 섹션은 제목 없이 내용만 추가
            else if (!section.title) {
              console.log('Processing section without title');
              // 제목이 없는 섹션도 내용은 추가
              if (section.paragraphs && Array.isArray(section.paragraphs)) {
                section.paragraphs.forEach((paragraph: string, paraIndex: number) => {
                  text += `${paragraph}\n\n`;
                  console.log(`Added paragraph ${paraIndex} from untitled section:`, paragraph);
                });
              }
            }
           
           // 단락들이 있으면 처리
           if (section.paragraphs && Array.isArray(section.paragraphs)) {
             console.log('Processing paragraphs:', section.paragraphs);
             section.paragraphs.forEach((paragraph: string, paraIndex: number) => {
               text += `${paragraph}\n\n`;
               console.log(`Added paragraph ${paraIndex}:`, paragraph);
             });
           }
         });
      }
      
      console.log('Final text output:', text);
      return text.trim();
    } catch (e) {
      console.error('Error parsing chapter to text:', e);
      return String(chapterContent);
    }
  };

  // 텍스트를 구조화된 챕터 형태로 변환
  const parseTextToChapter = (text: string, originalChapterContent?: ChapterContent | string): ChapterContent => {
    try {
      console.log('parseTextToChapter input text:', text);
      
      const lines = text.split('\n').map(line => line.trim()).filter(line => line);
      console.log('Processed lines:', lines);
      
      const result: ChapterContent = {
        title: '',
        sections: []
      };
      
      let currentSection: ChapterSection | null = null;
      let currentParagraphs: string[] = [];
      let sectionIndex = 1;
      
      lines.forEach((line, lineIndex) => {
        console.log(`Processing line ${lineIndex}: "${line}"`);
        
        // 제목 처리 (# 으로 시작)
        if (line.startsWith('# ') && !line.startsWith('## ')) {
          result.title = line.substring(2).trim();
          console.log('Set title:', result.title);
        }
        // 섹션 제목 처리 (## 으로 시작)
        else if (line.startsWith('## ')) {
          // 이전 섹션이 있으면 저장
          if (currentSection && (currentParagraphs.length > 0 || (currentSection as ChapterSection).paragraphs.length > 0)) {
            const sectionToAdd: ChapterSection = {
              index: sectionIndex,
              title: (currentSection as ChapterSection).title,
              paragraphs: [...(currentSection as ChapterSection).paragraphs, ...currentParagraphs]
            };
            result.sections.push(sectionToAdd);
            console.log('Added section:', sectionToAdd);
            sectionIndex++;
          }
          
          // 새 섹션 시작
          const sectionTitle = line.substring(3).trim();
          console.log('New section title:', sectionTitle);
          
          // 편집자 메모 특별 처리
          if (sectionTitle.startsWith('📝 ')) {
            currentSection = {
              index: sectionIndex,
              title: '편집자 메모',
              paragraphs: []
            };
          } else {
            currentSection = {
              index: sectionIndex,
              title: sectionTitle,
              paragraphs: []
            };
          }
          currentParagraphs = [];
          console.log('Created new section:', currentSection);
        }
        // 섹션이 없는데 내용이 있는 경우 (제목 없는 첫 번째 섹션)
        else if (line && !currentSection) {
          // 첫 번째 섹션 생성 (제목 없음)
          currentSection = {
            index: sectionIndex,
            title: '',
            paragraphs: [line]
          };
          console.log('Created first section without title:', currentSection);
        }
        // 현재 섹션이 있고 일반 텍스트인 경우
        else if (line && currentSection) {
          currentParagraphs.push(line);
          console.log('Added paragraph to current section:', line);
        }
      });
      
      // 마지막 섹션 처리 - 현재 섹션의 기존 단락과 새로 추가된 단락을 모두 포함
      if (currentSection) {
        const allParagraphs = [...(currentSection as ChapterSection).paragraphs, ...currentParagraphs];
        if (allParagraphs.length > 0) {
          const finalSection: ChapterSection = {
            index: sectionIndex,
            title: (currentSection as ChapterSection).title,
            paragraphs: allParagraphs
          };
          result.sections.push(finalSection);
          console.log('Added final section:', finalSection);
        }
      }
      
      // 섹션 인덱스 재정렬 (1부터 시작)
      result.sections = result.sections.map((section: ChapterSection, index: number) => ({
        ...section,
        index: index + 1
      }));
      
      console.log('Final result:', result);
      return result;
    } catch (e) {
      console.error('Error parsing text to chapter:', e);
      // 실패 시 기본 구조 반환
      return {
        title: '',
        sections: []
      };
    }
  };

  // 파일 편집 시작
  const handleEditFile = (filepath: string, content: ChapterContent | string) => {
    setEditingFile(filepath);
    // 챕터 파일인 경우 텍스트로 변환하여 편집
    if (filepath.includes('chapter')) {
      setEditingContent(parseChapterToText(content));
    } else {
      setEditingContent(typeof content === 'string' ? content : JSON.stringify(content));
    }
  };

  // 파일 편집 취소
  const handleCancelEdit = () => {
    setEditingFile('');
    setEditingContent('');
  };

  // 파일 저장
  const handleSaveFile = async () => {
    if (!editingFile || !editingContent) return;

    try {
      setSavingFile(true);
      const file_name = editingFile.split('/').pop() || '';
      
      // 챕터 파일인 경우 텍스트를 구조화된 형태로 변환
      let contentToSave = editingContent;
      if (file_name.includes('chapter')) {
        // 원본 챕터 내용 찾기
        let originalContent: ChapterContent | string = '';
        interviewData?.interviews.forEach(interview => {
          interview.associated_files.forEach(file => {
            if (file.path === editingFile && file.chapter_content) {
              originalContent = file.chapter_content;
            }
          });
        });
        
        // 텍스트를 구조화된 형태로 변환
        const structuredContent = parseTextToChapter(editingContent, originalContent);
        contentToSave = JSON.stringify(structuredContent, null, 2);
      }
      
      const response = await saveInterviewFile?.(userId!, projectId!, episodeId!, file_name, contentToSave);
      
      if (response?.success) {
        await loadInterviewData(); // 데이터 새로고침
        setEditingFile('');
        setEditingContent('');
        alert('파일이 성공적으로 저장되었습니다!');
      } else {
        alert('파일 저장에 실패했습니다.');
      }
    } catch (err) {
      alert('파일 저장 중 오류가 발생했습니다.');
      console.error('File save error:', err);
    } finally {
      setSavingFile(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.userDetail}>
        <h1 className="admin-page-title">인터뷰 상세 정보</h1>
        <div className={styles.loadingSpinner}>로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.userDetail}>
        <h1 className="admin-page-title">인터뷰 상세 정보</h1>
        <div className={styles.errorMessage}>
          {error}
        </div>
      </div>
    );
  }

  if (!interviewData) {
    return (
      <div className={styles.userDetail}>
        <h1 className="admin-page-title">인터뷰 상세 정보</h1>
        <div className={styles.errorMessage}>
          인터뷰를 찾을 수 없습니다.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.userDetail}>
      <div className={styles.pageTitleRow}>
        <h1>인터뷰 상세 정보 관리</h1>
        <button onClick={handleGoBack} className={styles.btnBack}>
          {getBackButtonText()}
        </button>
      </div>

      <div className={styles.userDetailContent}>
        {/* 프로젝트 정보 섹션 */}
        <div className={styles.userInfoSection}>
          <div className={styles.sectionHeader}>
            <h2>프로젝트 정보</h2>
          </div>
          <div className={styles.basicInfoCard}>
            <div className={styles.compactGrid}>
              <div className={styles.infoItem}>
                <label>프로젝트 ID</label>
                <span className={styles.basicInfoValue}>{interviewData.project_info.id}</span>
              </div>
              <div className={styles.infoItem}>
                <label>제목</label>
                <span className={styles.basicInfoValue}>{interviewData.project_info.title}</span>
              </div>
              <div className={styles.infoItem}>
                <label>설명</label>
                <span className={styles.basicInfoValue}>{interviewData.project_info.description}</span>
              </div>
              <div className={styles.infoItem}>
                <label>생성일</label>
                <span className={styles.basicInfoValue}>
                  {new Date(interviewData.project_info.created_at).toLocaleString('ko-KR')}
                </span>
              </div>
              <div className={styles.infoItem}>
                <label>수정일</label>
                <span className={styles.basicInfoValue}>
                  {new Date(interviewData.project_info.updated_at).toLocaleString('ko-KR')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 인터뷰 정보 섹션 */}
        <div className={styles.userInfoSection}>
          <div className={styles.sectionHeader}>
            <h2>인터뷰 정보</h2>
            <div className={styles.sectionHeaderActions}>
              {!isEditing && (
                <>
                  {/* <button 
                    onClick={handleEdit} 
                    className={styles.iconButton}
                    title="편집"
                  >
                    <EditIcon />
                  </button> */}
                  <button 
                    onClick={handleDelete} 
                    className={styles.iconButton}
                    title="삭제"
                  >
                    <DeleteIcon />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className={styles.basicInfoCard}>
            <div className={styles.compactGrid}>
              <div className={styles.infoItem}>
                <label>에피소드 ID</label>
                <span className={styles.basicInfoValue}>{interviewData.episode_id}</span>
              </div>
              <div className={styles.infoItem}>
                <label>인터뷰 수</label>
                <span className={styles.basicInfoValue}>{interviewData.interview_count}개</span>
              </div>
              <div className={styles.infoItem}>
                <label>챕터 생성 여부</label>
                <span className={`${styles.statusBadge} ${interviewData.is_chapter_generated ? styles.completed : styles.pending}`}>
                  {interviewData.is_chapter_generated ? '생성됨' : '미생성'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <label>히스토리 수</label>
                <span className={styles.basicInfoValue}>{interviewData.history_count}개</span>
              </div>
            </div>
          </div>

          {/* 인터뷰 세션 상세 정보 */}
          {interviewData.interviews.map((interview, index) => (
            <div key={interview.id} className={styles.basicInfoCard}>
              <h3>인터뷰 세션 {index + 1}</h3>
              <div className={styles.compactGrid}>
                <div className={styles.infoItem}>
                  <label>세션 ID</label>
                  <span className={styles.basicInfoValue}>{interview.session_id}</span>
                </div>
                <div className={styles.infoItem}>
                  <label>파일 경로</label>
                  <span className={styles.basicInfoValue}>{interview.file_path}</span>
                </div>
                <div className={styles.infoItem}>
                  <label>파일 크기</label>
                  <span className={styles.basicInfoValue}>
                    {formatFileSize(interview.file_size)} ({interview.file_size_mb.toFixed(2)} MB)
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <label>파일 존재 여부</label>
                  <span className={`${styles.statusBadge} ${interview.file_exists ? styles.completed : styles.pending}`}>
                    {interview.file_exists ? '존재' : '없음'}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <label>생성일</label>
                  <span className={styles.basicInfoValue}>
                    {new Date(interview.created_at).toLocaleString('ko-KR')}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <label>수정일</label>
                  <span className={styles.basicInfoValue}>
                    {new Date(interview.updated_at).toLocaleString('ko-KR')}
                  </span>
                </div>
              </div>

              {/* 연관 파일 정보 */}
              <div className={styles.associatedFilesSection}>
                <h4>인터뷰 파일</h4>
                <div className={styles.fileList}>
                  {interview.associated_files.map((file, fileIndex) => (
                    <div key={fileIndex} className={styles.fileItem}>
                      <div className={styles.fileHeader}>
                        <span className={styles.fileType}>{file.type}</span>
                        <span className={styles.fileTime}>
                          {file.file_modified_at && `${new Date(file.file_modified_at).toLocaleString('ko-KR')}`}
                        </span>
                        <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                      </div>
                      
                      {/* 채팅 내용 표시 */}
                      {file.chat_content && (
                        <div className={styles.chatContent}>
                          <h5>
                            채팅 기록
                            <button 
                              className={styles.btnRegenerateChapter}
                              onClick={() => handleRegenerateChapter()}
                              title="챕터 재생성"
                            >
                              <RegenerateIcon />
                              <span>이야기 다시쓰기</span>
                            </button>
                          </h5>
                          <div className={styles.chatHistory}>
                            {parseChatContent(file.chat_content).map((event, eventIndex) => (
                              <div key={eventIndex} className={styles.chatEventLine}>
                                <span className={styles.chatEventIcon}>
                                  {event.event === 'llm-response' ? '🤖' :
                                   event.event === 'stt-transcript' ? '🎤' :
                                   event.event === 'user-message' ? '👤' : '📝'}
                                </span>
                                <span className={styles.chatEventType}>
                                  {event.event === 'llm-response' ? 'AI' :
                                   event.event === 'stt-transcript' ? '음성' :
                                   event.event === 'user-message' ? '사용자' : event.event}
                                </span>
                                <span className={styles.chatEventContent}>
                                  {event.event === 'llm-response' ? event.data.response :
                                   event.event === 'stt-transcript' ? event.data.transcript :
                                   event.event === 'user-message' ? event.data.message : 
                                   JSON.stringify(event.data)}
                                </span>
                                {event.data.timestamp && (
                                  <span className={styles.chatTimestamp}>
                                    {formatTimestamp(event.data.timestamp)}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* 챕터 내용 표시 */}
                      {file.chapter_content && (
                        <div className={styles.chapterContent}>
                          <div className={styles.chapterHeader}>
                            <h5>챕터 내용</h5>
                            <div className={styles.chapterActions}>
                              {editingFile === file.path ? (
                                <>
                                  <button 
                                    className={styles.saveButton}
                                    onClick={handleSaveFile}
                                    disabled={savingFile}
                                    title={savingFile ? '저장 중...' : '저장'}
                                  >
                                    <SaveIcon />
                                  </button>
                                  <button 
                                    className={styles.cancelButton}
                                    onClick={handleCancelEdit}
                                    title="취소"
                                  >
                                    <CancelIcon />
                                  </button>
                                </>
                              ) : (
                                <button 
                                  className={styles.editButton}
                                  onClick={() => handleEditFile(file.path, file.chapter_content || '')}
                                  title="편집"
                                >
                                  <EditIcon />
                                </button>
                              )}
                              <button 
                                className={styles.deleteButton}
                                onClick={() => handleDeleteFile(file.path)}
                                title="파일 삭제"
                              >
                                <DeleteIcon />
                              </button>
                            </div>
                          </div>
                          {editingFile === file.path ? (
                            <div className={styles.chapterEditMode}>
                              <div className={styles.chapterEditInfo}>
                                <span className={styles.editInfoIcon}>📝</span>
                                <span>마크다운 형식으로 편집하세요. # 제목, ## 섹션제목, 일반 텍스트로 구성됩니다.</span>
                              </div>
                              <div className={styles.chapterEditLayout}>
                                <div className={styles.chapterEditInput}>
                                  <label>편집</label>
                                  <textarea
                                    className={styles.chapterTextarea}
                                    value={editingContent}
                                    onChange={(e) => setEditingContent(e.target.value)}
                                    placeholder="챕터 내용을 마크다운 형식으로 입력하세요...
# 제목
## 섹션 제목
내용을 입력하세요...

## 편집자 메모
편집자 메모를 입력하세요..."
                                    rows={15}
                                  />
                                </div>
                                <div className={styles.chapterEditPreview}>
                                  <label>미리보기</label>
                                  <div className={styles.chapterPreviewContent}>
                                    {(() => {
                                      try {
                                        const previewData = parseTextToChapter(editingContent, file.chapter_content);
                                        return (
                                          <div className={styles.chapterStructuredContent}>
                                            {previewData.title && (
                                              <div className={styles.chapterTitle}>
                                                <h4>{previewData.title}</h4>
                                              </div>
                                            )}
                                            {previewData.sections && previewData.sections.length > 0 ? (
                                              <div className={styles.chapterSections}>
                                                {previewData.sections.map((section: ChapterSection, sectionIndex: number) => (
                                                  <div 
                                                    key={sectionIndex} 
                                                    className={`${styles.chapterSection} ${
                                                      section.title === "편집자 메모" ? styles.editorMemo : ''
                                                    }`}
                                                  >
                                                    {section.title && (
                                                      <h5 className={styles.sectionTitle}>
                                                        {section.title === "편집자 메모" ? "📝 편집자 메모" : section.title}
                                                      </h5>
                                                    )}
                                                    {section.paragraphs && Array.isArray(section.paragraphs) && (
                                                      <div className={styles.sectionParagraphs}>
                                                        {section.paragraphs.map((paragraph: string, paraIndex: number) => (
                                                          <p key={paraIndex} className={styles.paragraph}>
                                                            {paragraph}
                                                          </p>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <div className={styles.chapterPreviewEmpty}>
                                                <span className={styles.previewEmptyIcon}>📝</span>
                                                <span>내용을 입력하면 여기에 미리보기가 표시됩니다.</span>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      } catch (e) {
                                        return (
                                          <div className={styles.chapterPreviewError}>
                                            <span className={styles.previewErrorIcon}>⚠️</span>
                                            <span>미리보기를 생성할 수 없습니다.</span>
                                          </div>
                                        );
                                      }
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className={styles.chapterStructuredContent}>
                              {(() => {
                                try {
                                  // chapter_content는 이미 파싱된 객체이므로 직접 사용
                                  const chapterData = file.chapter_content as ChapterContent;
                                  
                                  if (!chapterData || typeof chapterData !== 'object') {
                                    return <pre className={styles.chapterText}>챕터 내용을 불러올 수 없습니다.</pre>;
                                  }
                                  
                                  // 제목이 있으면 표시
                                  if (chapterData.title) {
                                    return (
                                      <div>
                                        <div className={styles.chapterTitle}>
                                          <h4>{chapterData.title}</h4>
                                        </div>
                                        {chapterData.sections && Array.isArray(chapterData.sections) && (
                                          <div className={styles.chapterSections}>
                                            {chapterData.sections.map((section: ChapterSection, sectionIndex: number) => (
                                              <div 
                                                key={sectionIndex} 
                                                className={`${styles.chapterSection} ${
                                                  section.title === "편집자 메모" ? styles.editorMemo : ''
                                                }`}
                                              >
                                                {section.title && (
                                                  <h5 className={styles.sectionTitle}>
                                                    {section.title === "편집자 메모" ? "📝 편집자 메모" : section.title}
                                                  </h5>
                                                )}
                                                {section.paragraphs && Array.isArray(section.paragraphs) && (
                                                  <div className={styles.sectionParagraphs}>
                                                    {section.paragraphs.map((paragraph: string, paraIndex: number) => (
                                                      <p key={paraIndex} className={styles.paragraph}>
                                                        {paragraph}
                                                      </p>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                  
                                  // 섹션만 있는 경우
                                  if (chapterData.sections && Array.isArray(chapterData.sections)) {
                                    return (
                                      <div className={styles.chapterSections}>
                                        {chapterData.sections.map((section: ChapterSection, sectionIndex: number) => (
                                          <div 
                                            key={sectionIndex} 
                                            className={`${styles.chapterSection} ${
                                              section.title === "편집자 메모" ? styles.editorMemo : ''
                                            }`}
                                          >
                                            {section.title && (
                                              <h5 className={styles.sectionTitle}>
                                                {section.title === "편집자 메모" ? "📝 편집자 메모" : section.title}
                                              </h5>
                                            )}
                                            {section.paragraphs && Array.isArray(section.paragraphs) && (
                                              <div className={styles.sectionParagraphs}>
                                                {section.paragraphs.map((paragraph: string, paraIndex: number) => (
                                                  <p key={paraIndex} className={styles.paragraph}>
                                                    {paragraph}
                                                  </p>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  }
                                  
                                  // 구조화되지 않은 경우 JSON 형태로 표시
                                  return <pre className={styles.chapterText}>{JSON.stringify(chapterData, null, 2)}</pre>;
                                } catch (e) {
                                  console.error('Error displaying chapter content:', e);
                                  return <pre className={styles.chapterText}>챕터 내용을 표시할 수 없습니다.</pre>;
                                }
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 영상 인터뷰 섹션 */}
        <div className={styles.userInfoSection}>
          <div className={styles.sectionHeader}>
            <h2>영상 인터뷰</h2>
          </div>
          <div className={styles.videoInterviewSection}>
            <div className={styles.videoInterviewCard}>
              <div className={styles.videoInterviewContent}>
                <div className={styles.videoInterviewIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className={styles.videoInterviewInfo}>
                  <h3>영상 인터뷰 녹화</h3>
                  <p>카메라를 통해 실시간으로 인터뷰를 녹화할 수 있습니다.</p>
                  <div className={styles.videoInterviewFeatures}>
                    <span className={styles.featureTag}>실시간 녹화</span>
                    <span className={styles.featureTag}>고품질 영상</span>
                    <span className={styles.featureTag}>자동 저장</span>
                  </div>
                </div>
                <div className={styles.videoInterviewActions}>
                  <button 
                    onClick={() => navigate(`/admin/users/${userId}/projects/${projectId}/episodes/${episodeId}/video-interview`)}
                    className={styles.videoInterviewButton}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                    </svg>
                    영상 인터뷰 시작
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PDF 생성 섹션 */}
        <div className={styles.userInfoSection}>
          <div className={styles.sectionHeader}>
            <h2>인터뷰 PDF 생성</h2>
          </div>
          <div className={styles.pdfGenerationSection}>
            <div className={styles.pdfGenerationCard}>
              <div className={styles.pdfGenerationContent}>
                <div className={styles.pdfGenerationIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20ZM8 12H16V14H8V12ZM8 16H13V18H8V16Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className={styles.pdfGenerationInfo}>
                  <h3>인터뷰 PDF 생성</h3>
                  <p>인터뷰 내용을 기반으로 자서전 PDF를 생성합니다.</p>
                  <div className={styles.pdfGenerationFeatures}>
                    <span className={styles.featureTag}>AI 기반 생성</span>
                    <span className={styles.featureTag}>고품질 PDF</span>
                    <span className={styles.featureTag}>자동 포맷팅</span>
                  </div>
                </div>
                <div className={styles.pdfGenerationActions}>
                  <button 
                    onClick={handleGeneratePDF}
                    disabled={generatingPDF}
                    className={styles.pdfGenerationButton}
                  >
                    {generatingPDF ? (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.spinningIcon}>
                          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z" fill="currentColor"/>
                        </svg>
                        PDF 생성 중...
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20ZM8 12H16V14H8V12ZM8 16H13V18H8V16Z" fill="currentColor"/>
                        </svg>
                        PDF 생성하기
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 이미지 섹션 */}
        <div className={styles.userInfoSection}>
          <div className={styles.sectionHeader}>
            <h2>사용자 이미지 ({interviewData.image_count}개)</h2>
          </div>
          <div className={styles.imagesSection}>
            {interviewData.images && interviewData.images.length > 0 ? (
              <div className={styles.imagesGrid}>
                {interviewData.images.map((image) => (
                  <div key={image.id} className={styles.imageCard}>
                    <div className={styles.imageDisplay}>
                      <img 
                        src={image.file_path} 
                        alt={`이미지 ${image.id}`}
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.style.display = 'none';
                          const errorDiv = target.nextElementSibling as HTMLDivElement;
                          if (errorDiv) {
                            errorDiv.style.display = 'block';
                          }
                        }}
                      />
                      <div style={{ 
                        display: 'none', 
                        color: '#6b7280', 
                        fontSize: '14px',
                        padding: '8px',
                        textAlign: 'center',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        backgroundColor: '#f9fafb'
                      }}>
                        이미지를 불러올 수 없습니다
                      </div>
                    </div>
                    <div className={styles.imageInfo}>
                      <h4 className={styles.imageTitle}>이미지 {image.id}</h4>
                      {image.description && (
                        <p className={styles.imageDescription}>{image.description}</p>
                      )}
                      <div className={styles.imageMeta}>
                        <span className={styles.imageDate}>
                          {new Date(image.created_at).toLocaleDateString('ko-KR')}
                        </span>
                        <span className={styles.imageId}>ID: {image.id}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.noImages}>
                <span className={styles.noImagesIcon}>🖼️</span>
                등록된 이미지가 없습니다.
              </div>
            )}
          </div>
        </div>
        
        {/* 히스토리 섹션 */}
        <div className={styles.userInfoSection}>
          <div className={styles.historyCollapsibleHeader} onClick={() => setHistoryExpanded(!historyExpanded)}>
            <h2>인터뷰 히스토리 ({interviewData.history_count}개)</h2>
            <span className={styles.historyExpandIcon}>{historyExpanded ? '▼' : '▶'}</span>
          </div>
          <div className={`${styles.historyCollapsibleContent} ${historyExpanded ? styles.expanded : styles.collapsed}`}>
            <div className={styles.historySection}>
              {interviewData.histories && interviewData.histories.length > 0 ? (
                <div className={styles.historyTimeline}>
                  {interviewData.histories.map((history, index) => (
                    <div key={history.id} className={`${styles.historyItem} ${styles[`action${history.action}` as keyof typeof styles]}`}>
                      <div className={styles.historyHeader}>
                        <div className={styles.historyMainInfo}>
                          <div className={styles.historyIcon}>
                            {history.action === 0 ? '🚀' : 
                             history.action === 1 ? '⏳' : 
                             history.action === 2 ? '✅' : '📝'}
                          </div>
                          <div>
                            <h4 className={styles.historyTitle}>
                              {getActionText(history.action)}
                            </h4>
                            <p className={styles.historySubtitle}>
                              세션 {history.session_code}
                            </p>
                          </div>
                        </div>
                        <div className={styles.historyDate}>
                          <div className={styles.historyDateMain}>
                            {new Date(history.created_at).toLocaleDateString('ko-KR')}
                          </div>
                          <div className={styles.historyDateSub}>
                            {new Date(history.created_at).toLocaleTimeString('ko-KR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                      
                      <div className={styles.historyContent}>
                        <span className={`${styles.historyActionBadge} ${styles[`action${history.action}` as keyof typeof styles]}`}>
                          {getActionText(history.action)}
                        </span>
                        <div className={styles.historySessionInfo}>
                          <span className={styles.historySessionLabel}>세션 코드</span>
                          <span className={styles.historySessionCode}>{history.session_code}</span>
                        </div>
                      </div>
                      
                      <div className={styles.historyProgress}>
                        <div className={styles.historyProgressBar}>
                          <div className={`${styles.historyProgressFill} ${styles[`action${history.action}` as keyof typeof styles]}`}></div>
                        </div>
                        <span className={styles.historyProgressText}>
                          {history.action === 0 ? '33%' : 
                          history.action === 1 ? '66%' : 
                          history.action === 2 ? '100%' : '0%'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.noHistory}>
                  <span className={styles.noHistoryIcon}>📊</span>
                  등록된 히스토리가 없습니다.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 편집 모드 액션 버튼 */}
        {isEditing && (
          <div className={styles.userInfoSection}>
            <div className={styles.editActions}>
              <button 
                onClick={handleCancel} 
                className={styles.iconButton}
                title="취소"
              >
                <CancelIcon />
              </button>
              <button 
                onClick={handleSave} 
                className={styles.iconButton}
                disabled={saving}
                title={saving ? '저장 중...' : '저장'}
              >
                <SaveIcon />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 삭제 확인 다이얼로그 */}
      <Confirm
        show={showDeleteConfirm}
        title="인터뷰 삭제 확인"
        message="정말로 이 인터뷰를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다."
        onClose={handleDeleteConfirm}
        confirmText="삭제하기"
        cancelText="취소"
      />

      {/* 파일 삭제 확인 다이얼로그 */}
      <Confirm
        show={showDeleteFileConfirm}
        title="파일 삭제 확인"
        message="정말로 이 파일을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다."
        onClose={handleDeleteFileConfirm}
        confirmText="삭제하기"
        cancelText="취소"
      />
    </div>
  );
};

export default InterviewDetail;
